import { useCallback, useEffect, useMemo } from 'react';
import { createRunStore } from '@inference.engine/agentic-react';
import { PageAgentIntro, type PageAgentChat, type PageAgentIntroContent } from '@/entities/agent';
import { useCurrentWorkspaceId } from '@/entities/workspace';
import { ChatStoreScope, useChatStoreApi } from '@/features/chats';
import { ChatSession } from '@/pages/StreamChatSession/ChatSession';

/** Puts text in the composer of the chat around it, focused, for the person to finish. */
function usePutInComposer() {
  const chatStore = useChatStoreApi();
  return useCallback(
    (text: string) => {
      chatStore.getState().setInputValue(text);
      // The composer is the chat's own and exposes no ref; Flow.Assist focuses its own this way.
      requestAnimationFrame(() =>
        // eslint-disable-next-line @typescript-eslint/no-deprecated -- the rule flags every ParentNode.querySelector call, whatever the type argument
        document.querySelector<HTMLElement>('[data-page-agent-panel] textarea')?.focus(),
      );
    },
    [chatStore],
  );
}

/** Puts each request the page asks for in the composer. */
function AskIntoComposer({ ask }: { ask: PageAgentChat['ask'] }) {
  const putInComposer = usePutInComposer();
  useEffect(() => {
    if (ask) putInComposer(ask.text);
  }, [ask, putInComposer]);
  return null;
}

/** The intro, inside the chat's store scope so a prompt can land in its composer. */
function Intro({ intro }: { intro: PageAgentIntroContent }) {
  const putInComposer = usePutInComposer();
  return <PageAgentIntro {...intro} onPrompt={putInComposer} />;
}

/** A page agent as Studio's own chat, in a store scope of its own so it leaves the app's chat alone. */
function PageAgentChatSession({ chat }: { chat: PageAgentChat }) {
  const workspaceId = useCurrentWorkspaceId() ?? '';
  const runStore = useMemo(createRunStore, [chat.chatId]);
  const { ask, intro, acceptsFiles, ...session } = chat;

  return (
    <ChatStoreScope>
      <AskIntoComposer ask={ask} />
      <ChatSession
        {...session}
        workspaceId={workspaceId}
        runStore={runStore}
        widgets={false}
        dropzone={acceptsFiles ?? false}
        titleFromFirstMessage={false}
        {...(intro ? { empty: <Intro intro={intro} /> } : {})}
      />
    </ChatStoreScope>
  );
}

/** Every page agent's chat. A different agent or conversation is a fresh chat, never a reused one. */
export function renderPageAgentChat(chat: PageAgentChat) {
  return <PageAgentChatSession key={`${chat.relay.agentId}:${chat.chatId}`} chat={chat} />;
}
